import { useState, useEffect } from "react";
import { useGroupFiltersQuery } from "../../../hooks/reports/queries/useDashboardQueries";
import { useDashboardMutations } from "../../../hooks/reports/mutations/useDashboardMutations";
import DashboardDateFilter from "./DashboardDateFilter";
import DashboardSingleDateFilter from "./DashboardSingleDateFilter";
import Button from "../../UI/Button";
import Loader from "../../UI/Loader";
import { DateFilterValue, ApplyFiltersRequest } from "../../../models/pageModels/GroupFilters";
import { isValidISODate } from "../../../utils/dateHelpers";

interface GlobalFiltersPanelProps {
    groupId: string;
    isRefreshing: boolean;
    refreshLimitReached?: boolean;
    refreshLimitMessage?: string | null;
}

const DATE_RANGE_LABELS: Record<string, string> = {
    ACTIVE_DATE: "Active Date",
    ACTIVITY_DATE: "Activity Date",
    ENROLL_DATE: "Enrollment Date",
};

const CUSTOM_FILTER_LABELS: Record<string, string> = {
    PARTICIPENT_BIRTH_DATE: "Birth Date",
    PARTICIPANT_CREATED: "Created Date",
    PARTICIPANT_ACTIVATED: "Activated Date",
    PARTICIPANT_DEACTIVATED: "Deactivated Date",
    HOUSEHOLD_MEMBER_BIRTH_DATE: "Household Member Birth Date",
    PROGRAM_ENROLLED_DATE: "Enrollment Started",
    PROGRAM_UNENROLLED_DATE: "Enrollment Ended",
    PROGRAM_ENROLLED_DURING_DATE: "Enrolled During",
};

const getMutationErrorMessage = (error: unknown) => {
    const maybeAxiosError = error as { response?: { data?: { error?: string; message?: string } } };
    const message = maybeAxiosError.response?.data?.error || maybeAxiosError.response?.data?.message;
    return message?.replace(/^BadRequest:\s*/, "") || "Failed to apply filters.";
};

const GlobalFiltersPanel = ({
    groupId,
    isRefreshing,
    refreshLimitReached = false,
    refreshLimitMessage = null
}: GlobalFiltersPanelProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [dateRangeValues, setDateRangeValues] = useState<Record<string, DateFilterValue | null>>({});
    const [customFilterValues, setCustomFilterValues] = useState<Record<string, DateFilterValue | null>>({});
    const [ageSummaryDate, setAgeSummaryDate] = useState<string>("");
    const [validationError, setValidationError] = useState<string | null>(null);

    const { data, isLoading, isError } = useGroupFiltersQuery(groupId, isExpanded);
    const { applyFiltersMutation } = useDashboardMutations(groupId);

    useEffect(() => {
        if (!isExpanded) {
            setDateRangeValues({});
            setCustomFilterValues({});
            setAgeSummaryDate("");
            setValidationError(null);
        }
    }, [isExpanded]);

    const dateRangeEntries = data
        ? Object.entries(data.commonFilters.dateRanges).filter(([, info]) => info.available)
        : [];
    const customFilterEntries = data
        ? Object.entries(data.commonFilters.customDateFilters).filter(([, info]) => info.available)
        : [];
    const hasAgeSummary = data?.commonFilters.ageSummary.available ?? false;
    const hasAnyFilters = dateRangeEntries.length > 0 || customFilterEntries.length > 0 || hasAgeSummary;

    const hasDateRangeValues = dateRangeEntries.some(([key]) => dateRangeValues[key] != null);
    const hasCustomFilterValues = customFilterEntries.some(([t]) => customFilterValues[t] != null);
    const hasAnyValue = hasDateRangeValues || hasCustomFilterValues || ageSummaryDate !== "";

    const handleDateRangeChange = (key: string) => (value: DateFilterValue | null) => {
        setDateRangeValues(prev => ({ ...prev, [key]: value }));
        setValidationError(null);
    };

    const handleCustomFilterChange = (filterType: string) => (value: DateFilterValue | null) => {
        setCustomFilterValues(prev => ({ ...prev, [filterType]: value }));
        setValidationError(null);
    };

    const handleAgeSummaryDateChange = (value: string) => {
        setAgeSummaryDate(value);
        setValidationError(null);
    };

    const handleClearAll = () => {
        if (isRefreshing) return;
        setDateRangeValues({});
        setCustomFilterValues({});
        setAgeSummaryDate("");
        setValidationError(null);
    };

    const filterNonNull = (record: Record<string, DateFilterValue | null>): Record<string, DateFilterValue> =>
        Object.fromEntries(Object.entries(record).filter(([, v]) => v !== null)) as Record<string, DateFilterValue>;

    const handleApply = () => {
        if (isRefreshing || refreshLimitReached) return;

        const literalDateFilters = [...Object.values(dateRangeValues), ...Object.values(customFilterValues)]
            .filter((value): value is Exclude<DateFilterValue, { mode: "RELATIVE" }> => value !== null && !("mode" in value));
        const containsInvalidDate = literalDateFilters.some(({ from, to }) =>
            (from !== "" && !isValidISODate(from)) || (to !== "" && !isValidISODate(to))
        );

        if (containsInvalidDate || (ageSummaryDate !== "" && !isValidISODate(ageSummaryDate))) {
            setValidationError("Enter valid dates in YYYY-MM-DD format with a four-digit year.");
            return;
        }

        setValidationError(null);

        const request: ApplyFiltersRequest = {};

        const dateRangesPayload = filterNonNull(dateRangeValues);
        if (Object.keys(dateRangesPayload).length > 0) request.dateRanges = dateRangesPayload;

        const customFiltersPayload = filterNonNull(customFilterValues);
        if (Object.keys(customFiltersPayload).length > 0) request.customDateFilters = customFiltersPayload;

        if (ageSummaryDate) request.ageSummaryDate = ageSummaryDate;

        applyFiltersMutation.mutate(request);
    };

    return (
        <div className="global-filters-panel">
            {/* Header - always visible, toggles body */}
            <div
                className="global-filters-header"
                onClick={() => setIsExpanded(prev => !prev)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") setIsExpanded(prev => !prev); }}
            >
                <div className="global-filters-header-left">
                    <span className={`global-filters-chevron ri-arrow-${isExpanded ? "down" : "right"}-s-line`} />
                    <span className="global-filters-title">Common Report Filters</span>
                    <span className="global-filters-subtitle">
                        Use these shared filters to update matching reports on this dashboard. Applying changes refreshes the dashboard.
                    </span>
                </div>
            </div>

            {/* Body */}
            {isExpanded && (
                <div className="global-filters-body">
                    {isLoading && <Loader />}
                    {isError && (
                        <p className="global-filters-message error">Failed to load available filters.</p>
                    )}
                    {data && !hasAnyFilters && (
                        <p className="global-filters-message muted">
                            No common date filters found. Reports in this group don't share a common date filter.
                        </p>
                    )}

                    {data && hasAnyFilters && (
                        <>
                            {isRefreshing && (
                                <p className="global-filters-message warning">
                                    Dashboard refresh is in progress. Filters can be changed after the refresh finishes.
                                </p>
                            )}
                            {refreshLimitReached && (
                                <p className="global-filters-message warning">
                                    {refreshLimitMessage || "This dashboard has used its refresh for today."}
                                </p>
                            )}
                            <div className="global-filters-row">
                                {dateRangeEntries.map(([key]) => (
                                    <DashboardDateFilter
                                        key={key}
                                        id={`global-date-range-${key}`}
                                        label={DATE_RANGE_LABELS[key] ?? key}
                                        value={dateRangeValues[key] ?? null}
                                        onChange={handleDateRangeChange(key)}
                                        disabled={isRefreshing}
                                    />
                                ))}
                                {customFilterEntries.map(([filterType]) => (
                                    <DashboardDateFilter
                                        key={filterType}
                                        id={`global-filter-${filterType}`}
                                        label={CUSTOM_FILTER_LABELS[filterType] || filterType}
                                        value={customFilterValues[filterType] ?? null}
                                        onChange={handleCustomFilterChange(filterType)}
                                        disabled={isRefreshing}
                                    />
                                ))}
                                {hasAgeSummary && (
                                    <DashboardSingleDateFilter
                                        id="global-age-as-of-date"
                                        label="Age As Of Date"
                                        value={ageSummaryDate}
                                        onChange={handleAgeSummaryDateChange}
                                        disabled={isRefreshing}
                                    />
                                )}
                            </div>

                            {/* Actions row - always below all filters */}
                            <div className="global-filters-actions">
                                <Button
                                    id="apply-global-filters"
                                    text="Apply to All"
                                    onClick={handleApply}
                                    loading={applyFiltersMutation.isPending}
                                    disabled={!hasAnyValue || isRefreshing || refreshLimitReached}
                                />
                                {hasAnyValue && (
                                    <Button
                                        id="clear-global-filters"
                                        text="Clear"
                                        className="secondary"
                                        onClick={handleClearAll}
                                        disabled={applyFiltersMutation.isPending || isRefreshing}
                                    />
                                )}
                                {(validationError || applyFiltersMutation.isError) && (
                                    <span className="global-filters-error">
                                        {validationError || getMutationErrorMessage(applyFiltersMutation.error)}
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default GlobalFiltersPanel;
